import { useCallback, useEffect, useState } from 'react';
import { ErrorBanner } from '@/shared/ui/ErrorBanner';
import { useNotifications } from '@/app/providers/notifications';
import { createToken, listTokens, revokeToken } from '@/features/settings/services/tokensApi';
import { safeErrorMessage } from '@/shared/utils/errors';
import {
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  EmptyState,
  Input,
  Select,
  Spinner,
} from '@/shared/ui';
import type { CreatedDeveloperToken, DeveloperToken } from '@/shared/types';
import { formatShortDate } from '@/shared/utils/format';

// Mirrors the engine's `expires_in_days` bounds (1..730). "Never" sends nothing at
// all and lets the engine apply its own default, rather than encoding it here.
const EXPIRY_OPTIONS: ReadonlyArray<{ label: string; days: number | null }> = [
  { label: 'Never', days: null },
  { label: '30 days', days: 30 },
  { label: '90 days', days: 90 },
  { label: '1 year', days: 365 },
];

const formatDate = (value: string | null): string => formatShortDate(value, '—');

function formatLastUsed(value: string | null): string {
  return value ? formatDate(value) : 'Never used';
}

export default function DeveloperTokensPage() {
  const { pushNotification } = useNotifications();
  const [tokens, setTokens] = useState<DeveloperToken[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [expiryDays, setExpiryDays] = useState<number | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // The one and only copy of a freshly minted secret. Held in component state
  // deliberately — never persisted anywhere — and cleared when dismissed.
  const [created, setCreated] = useState<CreatedDeveloperToken | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  // The whole token, not just its id: the dialog names it, and holding the row
  // means the copy survives a refresh that reorders the list underneath.
  const [pendingRevoke, setPendingRevoke] = useState<DeveloperToken | null>(null);

  const refresh = useCallback(async () => {
    try {
      setTokens(await listTokens());
      setLoadError(null);
    } catch (err) {
      setLoadError(safeErrorMessage(err, "We couldn't load your developer tokens."));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleCreate = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setCreateError('Give the token a name so you can recognise it later.');
      return;
    }
    setIsCreating(true);
    setCreateError(null);
    try {
      const token = await createToken(trimmed, expiryDays);
      setCreated(token);
      setName('');
      setExpiryDays(null);
      await refresh();
    } catch (err) {
      setCreateError(safeErrorMessage(err, "We couldn't create that token."));
    } finally {
      setIsCreating(false);
    }
  };

  const handleCopy = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      pushNotification({ message: `${label} copied to clipboard.` });
    } catch {
      // Clipboard access can be blocked (insecure context, denied permission).
      // The value is on screen and selectable, so say that instead of failing silently.
      pushNotification({ message: `Couldn't copy automatically — select the ${label.toLowerCase()} and copy it manually.` });
    }
  };

  const handleRevoke = async (token: DeveloperToken) => {
    setPendingRevoke(null);
    setRevokingId(token.id);
    try {
      await revokeToken(token.id);
      // If the revoked token is the one whose secret is still on screen, that panel
      // is now misleading — drop it.
      setCreated((current) => (current?.id === token.id ? null : current));
      await refresh();
      pushNotification({ message: `"${token.name}" revoked.` });
    } catch (err) {
      setLoadError(safeErrorMessage(err, "We couldn't revoke that token."));
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <section className="tokens-page">
      {/* h2, not h1: this renders inside SettingsPage, which owns the page's h1. */}
      <div className="settings-section-head">
        <h2>Developer tokens</h2>
        <p className="settings-section-head__sub">
          Long-lived credentials for the <code>valoi</code> CLI and CI. A token acts as your
          account, so treat it like a password.
        </p>
      </div>

      {loadError && <ErrorBanner message={loadError} onDismiss={() => setLoadError(null)} />}

      {created && (
        <Card className="token-reveal" role="alert" aria-live="assertive">
          <CardHeader title="Copy your token now" />
          <p className="token-reveal__warning">
            This is the only time we can show it. We store only a hash, so if you lose it
            you'll need to create a new one.
          </p>

          <div className="token-reveal__row">
            <code className="token-reveal__secret">{created.token}</code>
            <Button variant="primary" onClick={() => void handleCopy(created.token, 'Token')}>
              Copy
            </Button>
          </div>

          <p className="field__hint">Then authenticate the CLI:</p>
          <div className="token-reveal__row">
            <code className="token-reveal__command">
              valoi auth login --token {created.token}
            </code>
            <Button
              onClick={() =>
                void handleCopy(`valoi auth login --token ${created.token}`, 'Command')
              }
            >
              Copy
            </Button>
          </div>

          <Button
            variant="ghost"
            className="token-reveal__dismiss"
            onClick={() => setCreated(null)}
          >
            I've saved it — dismiss
          </Button>
        </Card>
      )}

      <Card>
        <CardHeader title="Create a token" />
        <form className="token-form" onSubmit={handleCreate}>
          <Input
            label="Name"
            type="text"
            value={name}
            maxLength={100}
            placeholder="laptop, ci, …"
            onChange={(event) => setName(event.target.value)}
            disabled={isCreating}
          />
          <Select
            label="Expires"
            value={expiryDays === null ? 'never' : String(expiryDays)}
            onChange={(event) =>
              setExpiryDays(event.target.value === 'never' ? null : Number(event.target.value))
            }
            disabled={isCreating}
          >
            {EXPIRY_OPTIONS.map((option) => (
              <option key={option.label} value={option.days === null ? 'never' : option.days}>
                {option.label}
              </option>
            ))}
          </Select>
          <Button type="submit" variant="primary" loading={isCreating}>
            {isCreating ? 'Creating…' : 'Create token'}
          </Button>
        </form>
        {createError && <ErrorBanner message={createError} onDismiss={() => setCreateError(null)} />}
      </Card>

      <Card>
        <CardHeader title="Your tokens" />

        {isLoading ? (
          <Spinner label="Loading tokens" />
        ) : tokens.length === 0 ? (
          <EmptyState
            title="No developer tokens yet"
            hint={
              <>
                Create one above to use the <code>valoi</code> CLI.
              </>
            }
            compact
          />
        ) : (
          /* The table scrolls inside this div rather than being the scroller
             itself: a blockified <table> still leaks its children's intrinsic
             width to the page, which is how six columns ended up scrolling the
             whole document sideways on a phone. The same wrapper DataTable
             puts round its own table. */
          <div className="token-table-scroll">
            <table className="token-table">
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col">Token</th>
                  <th scope="col">Created</th>
                  <th scope="col">Last used</th>
                  <th scope="col">Expires</th>
                  <th scope="col">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {tokens.map((token) => (
                  <tr key={token.id}>
                    <td>{token.name}</td>
                    <td>
                      <code>{token.tokenPrefix}…</code>
                    </td>
                    <td>{formatDate(token.createdAt)}</td>
                    <td>{formatLastUsed(token.lastUsedAt)}</td>
                    <td>{token.expiresAt ? formatDate(token.expiresAt) : 'Never'}</td>
                    <td>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => setPendingRevoke(token)}
                        loading={revokingId === token.id}
                      >
                        {revokingId === token.id ? 'Revoking…' : 'Revoke'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <ConfirmDialog
        open={pendingRevoke !== null}
        title={pendingRevoke ? `Revoke "${pendingRevoke.name}"?` : 'Revoke this token?'}
        description="Anything using this token — scripts, CI — stops working immediately. This cannot be undone."
        confirmLabel="Revoke token"
        cancelLabel="Keep it"
        onConfirm={() => {
          if (pendingRevoke) void handleRevoke(pendingRevoke);
        }}
        onCancel={() => setPendingRevoke(null)}
      />
    </section>
  );
}
